import { useCallback, useId, useMemo, useRef, useState } from "react";
import type { SyntheticEvent } from "react";
import {
  Alert,
  Box,
  Button,
  Group,
  Image,
  Loader,
  SegmentedControl,
  Stack,
  Text,
  UnstyledButton,
} from "@mantine/core";
import type {
  LegacyReportCropInput,
  LegacyReportManifest,
  LegacyReportReviewAsset,
} from "../../../types/legacyReportReview";
import { useLegacyReportBlob } from "../../../hooks/useLegacyReportBlob";
import { useLegacyReportCropSelection } from "../../../hooks/useLegacyReportCropSelection";
import { isSourceCropWithinPage } from "../../../utils/legacyReportCrop";

interface Props {
  page: LegacyReportManifest["pages"][number];
  asset: LegacyReportReviewAsset;
  region: LegacyReportCropInput["region"];
  disabled: boolean;
  onChange: (region: LegacyReportCropInput["region"]) => void;
  onReadyChange: (ready: boolean) => void;
  onPendingChange: (pending: boolean) => void;
}

export default function LegacyReportCropSelector({
  page,
  asset,
  region,
  disabled,
  onChange,
  onReadyChange,
  onPendingChange,
}: Props) {
  const { url, failed, load } = useLegacyReportBlob(asset.contentUrl);
  const [decoded, setDecoded] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [viewSize, setViewSize] = useState("fit");
  const imageRef = useRef<HTMLImageElement>(null);
  const instructionId = useId();
  const enabled = useMemo(
    () => decoded && !failed && !imageFailed && !disabled,
    [decoded, failed, imageFailed, disabled]
  );
  const selection = useLegacyReportCropSelection({
    page,
    region,
    enabled,
    onChange,
    onPendingChange,
  });
  const selectionStyle = useMemo(
    () => ({
      left: `${(selection.visibleRegion.x / page.width) * 100}%`,
      top: `${(selection.visibleRegion.y / page.height) * 100}%`,
      width: `${(selection.visibleRegion.width / page.width) * 100}%`,
      height: `${(selection.visibleRegion.height / page.height) * 100}%`,
      border: `2px ${
        selection.first ? "dashed" : "solid"
      } var(--mantine-primary-color-filled)`,
      background: "var(--mantine-primary-color-light)",
      pointerEvents: "none" as const,
    }),
    [selection.visibleRegion, selection.first, page.width, page.height]
  );
  const sourceStyle = useMemo(
    () => ({
      maxWidth:
        viewSize === "fit"
          ? `calc(60vh * ${page.width / page.height})`
          : "100%",
      display: "block",
      cursor: enabled ? "crosshair" : "default",
      overflow: "hidden",
      userSelect: "none" as const,
    }),
    [viewSize, page.width, page.height, enabled]
  );
  const markerStyle = useMemo(
    () => ({
      left: `${(selection.cursor.x / page.width) * 100}%`,
      top: `${(selection.cursor.y / page.height) * 100}%`,
      transform: "translate(-50%, -50%)",
      pointerEvents: "none" as const,
      border: "2px solid var(--mantine-primary-color-filled)",
      borderRadius: "50%",
      background: "var(--mantine-color-body)",
    }),
    [selection.cursor, page.width, page.height]
  );
  const regionVisible = useMemo(
    () => isSourceCropWithinPage(selection.visibleRegion, page),
    [selection.visibleRegion, page]
  );
  const handleImageError = useCallback(() => {
    setDecoded(false);
    setImageFailed(true);
    onReadyChange(false);
  }, [onReadyChange]);
  const handleImageLoad = useCallback(
    async (event: SyntheticEvent<HTMLImageElement>) => {
      const image = event.currentTarget;
      try {
        if (typeof image.decode === "function") await image.decode();
        if (imageRef.current !== image) return;
        if (
          image.naturalWidth !== asset.width ||
          image.naturalHeight !== asset.height ||
          image.naturalWidth < 1
        ) {
          handleImageError();
          return;
        }
        setDecoded(true);
        setImageFailed(false);
        onReadyChange(true);
      } catch {
        if (imageRef.current === image) handleImageError();
      }
    },
    [asset.width, asset.height, handleImageError, onReadyChange]
  );
  const handleRetry = useCallback(() => {
    setDecoded(false);
    setImageFailed(false);
    onReadyChange(false);
    void load();
  }, [load, onReadyChange]);
  const handleViewSize = useCallback((value: string) => setViewSize(value), []);
  return (
    <Stack gap="sm">
      <Text id={instructionId} size="sm">
        Click or tap one corner of the graphic, then the opposite corner.
        Include the complete graphic, every label, and the entire legend. With a
        keyboard, focus the page, use arrow keys, and press Enter to set each
        corner. Hold Shift for smaller adjustments.
      </Text>
      <Group justify="space-between">
        <SegmentedControl
          aria-label="Source page view size"
          data={[
            { label: "Fit page", value: "fit" },
            { label: "Larger view", value: "large" },
          ]}
          value={viewSize}
          onChange={handleViewSize}
        />
        <Button
          variant="subtle"
          onClick={selection.handleFullPage}
          disabled={!enabled}
        >
          Use entire page
        </Button>
      </Group>
      {failed || imageFailed ? (
        <Alert color="red" title="Source page unavailable">
          The verified page image must load before this region can be saved.
          <Button onClick={handleRetry} variant="light" mt="sm" display="block">
            Retry source page
          </Button>
        </Alert>
      ) : (
        <>
          {!decoded && (
            <Loader aria-label="Loading verified source page" size="sm" />
          )}
          {url && (
            <UnstyledButton
              type="button"
              pos="relative"
              w="100%"
              mx="auto"
              style={sourceStyle}
              aria-label={`Select graphic corners on source page ${page.pageNumber}`}
              aria-describedby={instructionId}
              aria-disabled={!enabled}
              onClick={selection.handleClick}
              onMouseMove={selection.handleMouseMove}
              onKeyDown={selection.handleKeyDown}
              onFocus={selection.handleFocus}
            >
              <Image
                ref={imageRef}
                src={url}
                alt={`Verified original report page ${page.pageNumber}`}
                w="100%"
                h="auto"
                draggable={false}
                style={{ display: "block" }}
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
              {decoded && regionVisible && (
                <Box
                  component="span"
                  pos="absolute"
                  style={selectionStyle}
                  data-testid="source-crop-region"
                />
              )}
              {decoded && (selection.first || selection.keyboardCursor) && (
                <Box
                  component="span"
                  pos="absolute"
                  w={12}
                  h={12}
                  style={markerStyle}
                  aria-hidden
                />
              )}
            </UnstyledButton>
          )}
          {decoded && (
            <Group justify="space-between">
              <Text size="sm" fw={500} aria-live="polite">
                {selection.first
                  ? "First corner set. Choose the opposite corner to finish."
                  : "Selected region outlined. Check that no labels or legend are cut off."}
              </Text>
              {selection.first && (
                <Button
                  variant="default"
                  size="xs"
                  onClick={selection.handleCancel}
                >
                  Cancel corner selection
                </Button>
              )}
            </Group>
          )}
        </>
      )}
      {selection.selectionError && (
        <Alert color="orange">
          Choose a different opposite corner so the graphic has a visible area.
        </Alert>
      )}
    </Stack>
  );
}
